import type { ActiveAnalysis } from "../../../../../api/useActiveAnalysesStream";
import t from "../../../../../i18n";
import type { MetrologyStatus } from "diagnostics-domain/quantity/metrology";
import type { MetrologyOverrides } from "../../../../../api/useMetrologyOverrides";
import type { Quantity, QuantityIndex } from "../../../../../api/useQuantities";
import { getAnalysisQuantityMetrologyStatus } from "../../../metrology";

export enum InstrumentationState {
  MISSING_MEASURES = "MISSING_MEASURES",
  INVALID_MEASURES = "INVALID_MEASURES",
  UNAVAILABLE_FORMULAS = "UNAVAILABLE_FORMULAS",
}

export type QuantityInvalidity = Exclude<
  MetrologyStatus,
  "VALID_AUTO" | "VALID_MANUAL" | "VALID_PARTIAL_INPUTS"
>;

export type InstrumentationIndicator = {
  type: QuantityInvalidity | InstrumentationState;
  quantityName: string;
};

export type InstrumentationIndicators = InstrumentationIndicator[];

export const instrumentationIndicatorCountLabels: Record<
  InstrumentationState,
  (count: number) => string
> = {
  [InstrumentationState.MISSING_MEASURES]: (count = 1) =>
    t("summary.instrumentation.state.missing_measure", count),
  [InstrumentationState.UNAVAILABLE_FORMULAS]: (count = 1) =>
    t("summary.instrumentation.state.unavailable_formula", count),
  [InstrumentationState.INVALID_MEASURES]: (count = 1) =>
    t("summary.instrumentation.state.invalid_measure", count),
};

export const instrumentationIndicatorLabels: Omit<
  Record<InstrumentationState | QuantityInvalidity, string | ((count: number) => string)>,
  InstrumentationState.INVALID_MEASURES
> = {
  ["INVALID_AUTO"]: t("measures.chart.legend.invalid_auto.name"),
  ["INVALID_AUTO_CROSS_FILTER"]: t("measures.chart.legend.invalid_auto.name"),
  ["INVALID_MANUAL"]: t("measures.chart.legend.invalid_manual.name"),
  ["INVALID_INHERITED"]: t("measures.chart.legend.invalid.inherited.name"),
  ["NO_VALIDATION"]: t("measures.chart.legend.no_validation.name"),
  [InstrumentationState.MISSING_MEASURES]:
    instrumentationIndicatorCountLabels[InstrumentationState.MISSING_MEASURES],
  [InstrumentationState.UNAVAILABLE_FORMULAS]:
    instrumentationIndicatorCountLabels[InstrumentationState.UNAVAILABLE_FORMULAS],
};

function defineInvalidQuantities(
  analysis: ActiveAnalysis,
  _metrologyOverrides: MetrologyOverrides,
  quantityIndex: QuantityIndex,
): InstrumentationIndicators {
  const indicators: InstrumentationIndicators = [];
  for (const id in analysis.quantityValues) {
    const quantity = quantityIndex[id];
    if (!quantity) continue;
    const status = getAnalysisQuantityMetrologyStatus(analysis, quantity);
    if (status !== "VALID_AUTO" && status !== "VALID_MANUAL" && status !== "VALID_PARTIAL_INPUTS") {
      indicators.push({ type: status, quantityName: quantity.name });
    }
  }
  return indicators;
}

function defineUnavailableFormulas(
  analysis: ActiveAnalysis,
  quantities: Quantity[],
): InstrumentationIndicators {
  return quantities.reduce<InstrumentationIndicators>((acc, q) => {
    if (q.type === "FORMULA" && !analysis.quantityValues[q.id])
      acc.push({ type: InstrumentationState.UNAVAILABLE_FORMULAS, quantityName: q.name });
    return acc;
  }, []);
}

function defineMissingMeasures(
  analysis: ActiveAnalysis,
  quantities: Quantity[],
): InstrumentationIndicators {
  return quantities.reduce<InstrumentationIndicators>((acc, q) => {
    if (q.type === "MEASURE" && analysis.quantityValues[q.id]?.measured === undefined)
      acc.push({ type: InstrumentationState.MISSING_MEASURES, quantityName: q.name });
    return acc;
  }, []);
}

export function defineInstrumentationIndicators(
  analysis: ActiveAnalysis,
  quantities: Quantity[],
  metrologyOverrides: MetrologyOverrides,
  quantityIndex: QuantityIndex,
): InstrumentationIndicators {
  return [
    ...defineMissingMeasures(analysis, quantities),
    ...defineInvalidQuantities(analysis, metrologyOverrides, quantityIndex),
    ...defineUnavailableFormulas(analysis, quantities),
  ];
}

export function countInstrumentationIndicators(indicators: InstrumentationIndicators) {
  return Object.entries(
    indicators.reduce<Record<InstrumentationState, number>>(
      (acc, c) => {
        switch (c.type) {
          case InstrumentationState.MISSING_MEASURES:
            acc[InstrumentationState.MISSING_MEASURES] += 1;
            break;
          case InstrumentationState.UNAVAILABLE_FORMULAS:
            acc[InstrumentationState.UNAVAILABLE_FORMULAS] += 1;
            break;
          case "INVALID_AUTO":
          case "INVALID_AUTO_CROSS_FILTER":
          case "INVALID_MANUAL":
          case "INVALID_INHERITED":
          case "NO_VALIDATION":
            acc[InstrumentationState.INVALID_MEASURES] += 1;
            break;
          default:
            break;
        }
        return acc;
      },
      {
        [InstrumentationState.MISSING_MEASURES]: 0,
        [InstrumentationState.UNAVAILABLE_FORMULAS]: 0,
        [InstrumentationState.INVALID_MEASURES]: 0,
      },
    ),
  );
}
